// <s-product-gallery> scrolls its [data-gallery-track] to the slide a thumbnail button targets
// (command="--show", data-index) and marks the visible slide's thumbnail with aria-current.
class ProductGallery extends HTMLElement {
  #abort = new AbortController();
  #rafId = 0;

  connectedCallback() {
    const { signal } = this.#abort;
    this.addEventListener('command', (event) => this.#onCommand(event), { signal });
    const track = this.#track();
    if (track) {
      track.addEventListener('scroll', () => this.#queueSync(), { signal, passive: true });
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort.abort();
    cancelAnimationFrame(this.#rafId);
  }

  #track() {
    return this.querySelector('[data-gallery-track]');
  }

  #slides() {
    const track = this.#track();
    return track ? Array.from(track.children) : [];
  }

  #onCommand(event) {
    if (event.command !== '--show') return;
    const index = Number(event.source?.dataset.index ?? 0);
    const track = this.#track();
    const slide = this.#slides()[index];
    if (!track || !slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = slide.offsetLeft - track.offsetLeft;
    track.scrollTo({ left: document.dir === 'rtl' ? -Math.abs(left) : left, behavior: reduce ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  }

  #queueSync() {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#sync());
  }

  #sync() {
    const track = this.#track();
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    this.#setCurrent(index);
    this.#setProgress(track);
  }

  // Slides the mobile progress bar across its rail in step with the track's scroll position.
  #setProgress(track) {
    const bar = this.querySelector('[data-gallery-progress]');
    if (!bar) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const ratio = maxScroll > 0 ? Math.min(1, Math.abs(track.scrollLeft) / maxScroll) : 0;
    const steps = Math.max(0, this.#slides().length - 1);
    const direction = document.dir === 'rtl' ? -1 : 1;
    bar.style.transform = `translateX(${direction * ratio * steps * 100}%)`;
  }

  #setCurrent(index) {
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', ProductGallery);
}
